import React from 'react'
import { HiEye, HiXMark, HiShieldCheck } from 'react-icons/hi2'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import { Button } from '../../../../components/Button'
import { Textarea } from '../../../../components/Textarea'
import { UserMultiSelect } from '../../../../components/Select/UserMultiSelect'
import { FaRegCommentDots, FaUsers } from 'react-icons/fa'

interface UserOption {
  value: number
  label: string
  name: string
  email: string
  avatar?: string
}

export interface Observer {
  id: number
  observador_fk?: number
  active: boolean
  mencionado?: boolean // false = gestor/chefe de setor (não pode ser removido)
  usuario?: {
    name: string
    email?: string
  }
  name?: string
}

export interface ManageObserversModalProps {
  isOpen: boolean
  isMinimized: boolean
  isLoading: boolean
  selectedObservers: number[]
  observersToRemove: number[]
  observersComment: string
  userOptions: UserOption[]
  currentObservers: Observer[]
  onMinimize: () => void
  onRestore: () => void
  onClose: () => void
  onConfirm: () => void
  onObserversChange: (observers: number[]) => void
  onCommentChange: (comment: string) => void
  onToggleObserverRemoval: (observerId: number) => void
}

/**
 * Modal para gerenciar observadores da demanda
 */
export const ManageObserversModal: React.FC<ManageObserversModalProps> = ({
  isOpen,
  isMinimized,
  isLoading,
  selectedObservers,
  observersToRemove,
  observersComment,
  userOptions,
  currentObservers,
  onMinimize,
  onRestore,
  onClose,
  onConfirm,
  onObserversChange,
  onCommentChange,
  onToggleObserverRemoval,
}) => {
  const activeObservers = currentObservers.filter(obs => obs.active)

  return (
    <BaseModal
      isOpen={isOpen}
      isMinimized={isMinimized}
      modalId='demand-add-observers'
      actionIcon={<HiEye className='w-4 h-4' />}
      toolbarActionId='addObservers'
      onMinimize={onMinimize}
      onRestore={onRestore}
      onClose={onClose}
      title='Gerenciar Observadores'
      description='Adicione ou remova usuários que devem observar esta demanda e receber notificações sobre suas atualizações.'
      useStandardLayout={true}
      size='lg'
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className='flex justify-end space-x-3 p-4'>
          <Button variant='outline' onClick={onClose} disabled={isLoading} className='min-w-20'>
            Cancelar
          </Button>
          <Button
            variant='primary'
            onClick={onConfirm}
            disabled={
              isLoading || (selectedObservers.length === 0 && observersToRemove.length === 0)
            }
            isLoading={isLoading}
            className='min-w-24'
          >
            {isLoading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      }
    >
      <div className='p-4 space-y-4'>
        {/* Adicionar Novos Observadores - SEÇÃO PRINCIPAL */}
        <div className='space-y-3'>
          <div className='flex items-center space-x-2'>
            <div className='text-gray-600 dark:text-gray-400 '>
              <FaUsers className='size-7'/>
            </div>
            <label className='block text-lg font-semibold text-gray-900 dark:text-gray-100'>
              Adicionar Observadores
            </label>
            <span className='text-sm text-gray-500'>(principal)</span>
          </div>
          <UserMultiSelect
            variant='user-multi'
            options={userOptions}
            value={selectedObservers}
            onChange={value => onObserversChange(value.map(v => Number(v)))}
            placeholder='Selecione usuários para observar esta demanda...'
            className='w-full'
            searchable={true}
            disabled={isLoading}
          />
          <p className='text-xs text-gray-500 dark:text-gray-500'>
            Selecione os usuários que devem receber notificações sobre atualizações desta demanda.
          </p>
        </div>

        {/* Observadores Atuais - SEÇÃO SECUNDÁRIA */}
        {activeObservers.length > 0 && (
          <div className='space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-2'>
                <label className='block text-sm font-semibold text-gray-700 dark:text-gray-300'>
                  Remover Observadores
                </label>
                <span className='text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded'>
                  Clique para marcar/desmarcar
                </span>
              </div>
              {observersToRemove.length > 0 && (
                <span className='text-sm text-red-600 dark:text-red-400 font-semibold bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded'>
                  {observersToRemove.length} marcado(s)
                </span>
              )}
            </div>

            <div className='bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3'>
              <div className='space-y-2 max-h-48 overflow-y-auto'>
                {activeObservers.map(observador => {
                  const observerId = observador.observador_fk || observador.id
                  const isMarkedForRemoval = observersToRemove.includes(observerId)
                  // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
                  const isManager = Boolean(observador.mencionado) // Gestor/chefe de setor

                  return (
                    <div
                      key={observador.id}
                      className={`flex items-center justify-between p-2 border rounded-lg transition-all duration-200 ${
                        isManager
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 cursor-not-allowed'
                          : isMarkedForRemoval
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 cursor-pointer'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                      }`}
                      onClick={() => !isManager && onToggleObserverRemoval(observerId)}
                    >
                      <div className='flex items-center space-x-3'>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isManager
                              ? 'bg-blue-100 dark:bg-blue-800'
                              : isMarkedForRemoval
                                ? 'bg-red-100 dark:bg-red-800'
                                : 'bg-teal-100 dark:bg-teal-800'
                          }`}
                        >
                          {isManager ? (
                            <HiShieldCheck className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                          ) : isMarkedForRemoval ? (
                            <HiXMark className='w-4 h-4 text-red-600 dark:text-red-400' />
                          ) : (
                            <HiEye className='w-4 h-4 text-teal-600 dark:text-teal-400' />
                          )}
                        </div>
                        <div>
                          <div className='flex items-center space-x-2'>
                            <span
                              className={`text-sm font-medium ${
                                isManager
                                  ? 'text-blue-700 dark:text-blue-300'
                                  : isMarkedForRemoval
                                    ? 'text-red-700 dark:text-red-300 line-through'
                                    : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {observador.usuario?.name ||
                                observador.name ||
                                `Usuário ID: ${observador.observador_fk || observador.id}`}
                            </span>
                            {isManager && (
                              <span className='text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium'>
                                Gestor
                              </span>
                            )}
                          </div>
                          {observador.usuario?.email && (
                            <p
                              className={`text-xs ${
                                isManager
                                  ? 'text-blue-500 dark:text-blue-400'
                                  : isMarkedForRemoval
                                    ? 'text-red-500 dark:text-red-400'
                                    : 'text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              {observador.usuario.email}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className='flex items-center space-x-2'>
                        {isManager ? (
                          <span className='text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-100 dark:bg-blue-800 px-2 py-0.5 rounded'>
                            🔒 Protegido
                          </span>
                        ) : isMarkedForRemoval ? (
                          <span className='text-xs text-red-600 dark:text-red-400 font-semibold bg-red-100 dark:bg-red-900 px-2 py-0.5 rounded'>
                            ✓ Será removido
                          </span>
                        ) : (
                          <span className='text-xs text-gray-500 dark:text-gray-400'>
                            Clique para remover
                          </span>
                        )}
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isManager
                              ? 'bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600'
                              : isMarkedForRemoval
                                ? 'bg-red-600 border-red-600 shadow-sm'
                                : 'border-gray-300 dark:border-gray-500 hover:border-red-400 dark:hover:border-red-500'
                          }`}
                        >
                          {isManager ? (
                            <HiShieldCheck className='w-3 h-3 text-blue-600 dark:text-blue-400' />
                          ) : (
                            isMarkedForRemoval && <HiXMark className='w-3 h-3 text-white' />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className='mt-3 pt-2 border-t border-gray-200 dark:border-gray-600'>
                <p className='text-xs text-gray-600 dark:text-gray-400 flex items-center justify-between'>
                  <span>
                    {activeObservers.length} observador(es) ativo(s)
                    {observersToRemove.length > 0 && (
                      <span className='ml-2 text-red-600 dark:text-red-400 font-medium'>
                        • {observersToRemove.length} selecionado(s) para remoção
                      </span>
                    )}
                  </span>
                </p>
                <p className='text-xs text-blue-600 dark:text-blue-400 font-medium mt-1'>
                  💡 Gestores são automaticamente adicionados e não podem ser removidos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Comentário opcional */}
        <div className='space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700'>
          <div className='flex items-center space-x-2'>
            <div className='text-gray-600 dark:text-gray-400'>
              <FaRegCommentDots className='size-5'/>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-600 dark:text-gray-400'>
                Comentário Adicional <span className='text-xs text-gray-400'>(opcional)</span>
              </label>
            </div>
          </div>
          <Textarea
            value={observersComment}
            onChange={onCommentChange}
            placeholder='Adicione um comentário sobre as alterações nos observadores (opcional)...'
            disabled={isLoading}
            className='w-full'
            rows={3}
          />
        </div>
      </div>
    </BaseModal>
  )
}
